// src/modules/app-views/crm/pages/Boards/modals/BoardSettingsModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useState } from 'react';
import { Form, Modal, Tabs } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import crmService from '../../../../service';
import useZustand from 'hooks/useZustand';
import { buildDefaultSeeds } from '../../../../seed';
import { generateHexColor } from 'utils/colors';
import ParametersTab from './tabs/ParametersTab';
import ColumnsTab from './tabs/ColumnsTab';
import FormFieldsTab from './tabs/FormFieldsTab';
import OffersTab from './tabs/OffersTab';
import CalendarTab from './tabs/CalendarTab';
import HolidaysTab from './tabs/HolidaysTab';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const DEFAULT_BUSINESS_HOURS = {
  monday:    { enabled: true,  open: '08:00', close: '18:00' },
  tuesday:   { enabled: true,  open: '08:00', close: '18:00' },
  wednesday: { enabled: true,  open: '08:00', close: '18:00' },
  thursday:  { enabled: true,  open: '08:00', close: '18:00' },
  friday:    { enabled: true,  open: '08:00', close: '18:00' },
  saturday:  { enabled: false, open: '08:00', close: '12:00' },
  sunday:    { enabled: false, open: null,    close: null },
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const BoardSettingsModal = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { show, setShow, group, board, setBoard, onBoardRemoved } = props;
  const storedUser = useZustand('auth', 'user');
  const storedParameters = useZustand('app', 'parameters');
  const accountId = storedUser?._id;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isEditing = !!board;
  const groupUid = group?.uid || group?._id;
  const boardGroups = storedParameters?.modules?.crm?.boardGroups || [];
  // True when editing an existing board that has no group assignment
  const isOrphaned = isEditing && !groupUid && !(board?.boardGroupUid);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('parameters');
  const [localColumns, setLocalColumns] = useState([]);
  const [fixedColumns, setFixedColumns] = useState(false);
  const [allowNewColumns, setAllowNewColumns] = useState(true);
  const [allowColumnEdit, setAllowColumnEdit] = useState(true);
  const [allowMoveCards, setAllowMoveCards] = useState(false);
  const [linkedOfferIds, setLinkedOfferIds] = useState([]);
  const [businessHours, setBusinessHours] = useState(DEFAULT_BUSINESS_HOURS);
  const [holidays, setHolidays] = useState([]);
  const [localFormFields, setLocalFormFields] = useState([]);
  const [newOpportunityMode, setNewOpportunityMode] = useState('standard');
  const [opportunityField, setOpportunityField] = useState([]);
  const [intrinsicFieldLabels, setIntrinsicFieldLabels] = useState({});
  const [saving, setSaving] = useState(false);
  // Resolved group for orphaned boards: allows the user to pick a group via the UI
  const [resolvedGroupUid, setResolvedGroupUid] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // Initialize form and local state AFTER the modal DOM is fully rendered.
  // Using afterOpenChange avoids the "useForm not connected" warning caused by
  // calling form.setFieldsValue before the <Form> element mounts.
  const handleAfterOpenChange = useCallback(
    open => {
      if (!open) return;

      if (board) {
        form.setFieldsValue({
          title: board.title,
          backColor: board.backColor || '#1890ff',
          description: board.description || '',
          teamAccessIds: board.teamAccessIds || [],
        });
        setResolvedGroupUid(groupUid || board.boardGroupUid || null);
        const sorted = [...(board.columns || [])].sort(
          (a, b) => (a.order || 0) - (b.order || 0),
        );
        setLocalColumns(sorted);
        setFixedColumns(board.fixedColumns !== false);
        setAllowNewColumns(board.allowNewColumns || false);
        setAllowColumnEdit(board.allowColumnEdit || false);
        setAllowMoveCards(board.allowMoveCards || false);
        setLinkedOfferIds(board.linkedOfferIds || []);
        setBusinessHours(board.businessHours || DEFAULT_BUSINESS_HOURS);
        setHolidays(board.holidays || []);
        const sortedFields = [...(board.formFields || [])].sort(
          (a, b) => (a.order || 0) - (b.order || 0),
        );
        setLocalFormFields(sortedFields);
        setNewOpportunityMode(board.newOpportunityMode || 'standard');
        setOpportunityField(board.opportunityField || []);
        setIntrinsicFieldLabels(board.intrinsicFieldLabels || {});
      } else {
        form.resetFields();
        form.setFieldsValue({ backColor: generateHexColor() });
        setResolvedGroupUid(groupUid || null);
        setLocalColumns(buildDefaultSeeds().boards[0].columns);
        setFixedColumns(true);
        setAllowNewColumns(false);
        setAllowColumnEdit(false);
        setAllowMoveCards(false);
        setLinkedOfferIds([]);
        setBusinessHours(DEFAULT_BUSINESS_HOURS);
        setHolidays([]);
        setLocalFormFields([]);
        setNewOpportunityMode('standard');
        setOpportunityField([]);
        setIntrinsicFieldLabels({});
      }
    },
    [board, form]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const backColor =
        typeof values.backColor === 'string'
          ? values.backColor
          : values.backColor?.toHexString?.() || '#1890ff';

      const columns = localColumns.map((col, i) => ({ ...col, order: i + 1 }));

      const formFields = localFormFields.map((f, i) => ({ ...f, order: i + 1 }));

      // If mode is 'custom' but no form fields exist, fallback to 'standard'
      const resolvedMode = (newOpportunityMode === 'custom' && formFields.length > 0)
        ? 'custom'
        : 'standard';

      // Custom mode requires at least one field in opportunityField
      if (resolvedMode === 'custom' && !opportunityField?.length) {
        setActiveTab('formFields');
        appService.message({ flag: 'w', text: 'required_fields' });
        return;
      }

      // Orphaned board requires the user to select a group before saving
      const finalGroupUid = resolvedGroupUid || groupUid;
      if (isOrphaned && !finalGroupUid) {
        appService.message({ flag: 'w', text: 'required_fields' });
        return;
      }

      const data = {
        title: values.title,
        backColor,
        description: values.description || '',
        fixedColumns,
        allowNewColumns,
        allowColumnEdit,
        allowMoveCards,
        boardGroupUid: finalGroupUid,
        columns,
        formFields,
        newOpportunityMode: resolvedMode,
        opportunityField: resolvedMode === 'custom' ? opportunityField : [],
        intrinsicFieldLabels,
        teamAccessIds: values.teamAccessIds || [],
        linkedOfferIds,
        businessHours,
        holidays,
      };

      setSaving(true);

      if (isEditing) {
        const boardUid = board.uid || board._id;
        await crmService.updateBoard(boardUid, data);
        appService.message({ flag: 's', text: 'saved_successfully' });
      } else {
        await crmService.addBoard(data);
        appService.message({ flag: 's', text: 'saved_successfully' });
      }

      setShow(false);
      setBoard(null);
    } catch (err) {
      // Validation error: switch to the tab that has the missing field and warn the user
      if (err?.errorFields?.length) {
        setActiveTab('parameters');
        appService.message({ flag: 'w', text: 'required_fields' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!board) return;
    const boardUid = board.uid || board._id;

    // Check for active CRM documents before allowing removal
    const hasActive = await crmService.checkBoardHasActiveDocuments(boardUid);
    if (hasActive) {
      appService.notification({ flag: 'w', title: 'action_blocked', description: 'board_has_active_documents' });
      return;
    }

    appService.modal({
      flag: 'confirm',
      title: 'remove_board',
      content: 'delete_board_description',
      okText: 'yes',
      cancelText: 'no',
      okButtonProps: { danger: true },
      onOk: async () => {
        await crmService.removeBoard(boardUid, accountId);
        appService.message({ flag: 's', text: 'removed_successfully' });
        setShow(false);
        setBoard(null);
        onBoardRemoved?.();
      },
    });
  };

  const handleArchive = async () => {
    if (!board) return;
    const boardUid = board.uid || board._id;

    appService.modal({
      flag: 'confirm',
      title: 'archive_board',
      content: 'archive_board_description',
      okText: 'yes',
      cancelText: 'no',
      onOk: async () => {
        await crmService.archiveBoard(boardUid, accountId);
        appService.message({ flag: 's', text: 'archived_successfully' });
        setShow(false);
        setBoard(null);
        onBoardRemoved?.();
      },
    });
  };

  const handleCancel = useCallback(() => {
    if (!saving) {
      setShow(false);
      setBoard(null);
    }
  }, [saving, setShow, setBoard]);

  const handleAfterClose = useCallback(() => {
    setActiveTab('parameters');
    setSaving(false);
    setLocalColumns([]);
    setFixedColumns(true);
    setAllowNewColumns(false);
    setAllowColumnEdit(false);
    setAllowMoveCards(false);
    setLinkedOfferIds([]);
    setBusinessHours(DEFAULT_BUSINESS_HOURS);
    setHolidays([]);
    setLocalFormFields([]);
    setNewOpportunityMode('standard');
    setOpportunityField([]);
    setIntrinsicFieldLabels({});
    setResolvedGroupUid(null);
    form.resetFields();
  }, [form]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const tabItems = [
    {
      key: 'parameters',
      label: translateX('parameters'),
      forceRender: true,
      children: (
        <ParametersTab
          form={form}
          isEditing={isEditing}
          isOrphaned={isOrphaned}
          boardGroups={boardGroups}
          resolvedGroupUid={resolvedGroupUid}
          setResolvedGroupUid={setResolvedGroupUid}
          onDelete={handleDelete}
          onArchive={handleArchive}
        />
      ),
    },
    {
      key: 'columns',
      label: translateX('columns'),
      children: (
        <ColumnsTab
          localColumns={localColumns}
          setLocalColumns={setLocalColumns}
          fixedColumns={fixedColumns}
          setFixedColumns={setFixedColumns}
          allowNewColumns={allowNewColumns}
          setAllowNewColumns={setAllowNewColumns}
          allowColumnEdit={allowColumnEdit}
          setAllowColumnEdit={setAllowColumnEdit}
          allowMoveCards={allowMoveCards}
          setAllowMoveCards={setAllowMoveCards}
        />
      ),
    },
    {
      key: 'formFields',
      label: translateX('form'),
      children: (
        <FormFieldsTab
          localFormFields={localFormFields}
          setLocalFormFields={setLocalFormFields}
          localColumns={localColumns}
          newOpportunityMode={newOpportunityMode}
          setNewOpportunityMode={setNewOpportunityMode}
          opportunityField={opportunityField}
          setOpportunityField={setOpportunityField}
          intrinsicFieldLabels={intrinsicFieldLabels}
          setIntrinsicFieldLabels={setIntrinsicFieldLabels}
        />
      ),
    },
    // {
    //   key: 'offers',
    //   label: translateX('offers'),
    //   forceRender: true,
    //   children: (
    //     <OffersTab
    //       linkedOfferIds={linkedOfferIds}
    //       setLinkedOfferIds={setLinkedOfferIds}
    //     />
    //   ),
    // },
    {
      key: 'calendar',
      label: translateX('calendar'),
      children: (
        <CalendarTab
          businessHours={businessHours}
          setBusinessHours={setBusinessHours}
        />
      ),
    },
    {
      key: 'holidays',
      label: translateX('holidays'),
      children: (
        <HolidaysTab
          holidays={holidays}
          setHolidays={setHolidays}
        />
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SettingOutlined />
          {translateX(isEditing ? 'edit_board' : 'new_board')}
        </span>
      }
      open={show}
      onOk={handleOk}
      onCancel={handleCancel}
      afterOpenChange={handleAfterOpenChange}
      afterClose={handleAfterClose}
      okText={translateX('confirm')}
      cancelText={translateX('cancel')}
      confirmLoading={saving}
      mask={{ closable: !saving }}
      destroyOnHidden
      width={580}
      styles={{
        body: {
          maxHeight: 'calc(80vh - 110px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
      style={{ top: '10vh' }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        className="parameters-modal-tabs"
      />
      <style>{`
        .parameters-modal-tabs .ant-tabs-content-holder { flex: 1; overflow: auto; min-height: 0; }
        .parameters-modal-tabs .ant-tabs-content { min-height: 100%; }
        .parameters-modal-tabs .ant-tabs-tabpane { padding-bottom: 8px; }
      `}</style>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default BoardSettingsModal;
